import React from 'react';

type ComponentModule<P> = { default: React.ComponentType<P> };

export type PreloadableComponent<P> = React.LazyExoticComponent<React.ComponentType<P>> & {
  /** Starts (or joins) the chunk download; resolves once the module is ready. */
  preload: () => Promise<void>;
};

/**
 * `React.lazy` with a `preload()` hook. Once the module has loaded, the lazy
 * component renders synchronously on its first render (the loader hands React
 * an already-resolved thenable), so preloading ahead of need means no Suspense
 * fallback frame at all. A failed load is forgotten so the next call retries.
 */
export function lazyWithPreload<P>(
  load: () => Promise<ComponentModule<P>>
): PreloadableComponent<P> {
  let loaded: ComponentModule<P> | undefined;
  let pending: Promise<ComponentModule<P>> | undefined;

  const start = (): Promise<ComponentModule<P>> => {
    if (!pending) {
      pending = load().then(
        mod => {
          loaded = mod;
          return mod;
        },
        error => {
          pending = undefined;
          throw error;
        }
      );
    }
    return pending;
  };

  const Lazy = React.lazy(() => {
    const ready = loaded;
    if (ready) {
      // Synchronous thenable: React reads the resolved module in the same render.
      return { then: (resolve: (mod: ComponentModule<P>) => void) => resolve(ready) } as never;
    }
    return start();
  });

  return Object.assign(Lazy, { preload: () => start().then(() => undefined) });
}
